import type { PayloadAction } from '@reduxjs/toolkit';

import { createSlice } from '@reduxjs/toolkit';

import type { IReboPipelineDetail, IReboPipelineRow } from 'src/types/rebo-pipeline';

export interface IReboPipelineState {
  isLoading: boolean;
  hasError: boolean;
  error: string;
  rows: IReboPipelineRow[];
  page: number;
  rowsPerPage: number;
  totalCount: number | null;
  hasNext: boolean;
  currentDetail: IReboPipelineDetail | null;
  detailLoading: boolean;
  detailHasError: boolean;
  detailError: string;
}

const initialState: IReboPipelineState = {
  isLoading: false,
  hasError: false,
  error: '',
  rows: [],
  page: 0,
  rowsPerPage: 30,
  totalCount: null,
  hasNext: false,
  currentDetail: null,
  detailLoading: false,
  detailHasError: false,
  detailError: '',
};

type SetReboPipelinePagePayload = {
  rows: IReboPipelineRow[];
  page: number;
  hasNext: boolean;
  totalCount?: number;
};

type SetReboPipelineDetailPayload = {
  currentDetail: IReboPipelineDetail | null;
};

export const reboPipelineSlice = createSlice({
  name: 'rebo-pipeline',
  initialState,
  reducers: {
    startLoading: (state) => {
      state.isLoading = true;
      state.hasError = false;
      state.error = '';
    },
    hasError: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.hasError = true;
      state.error = action.payload;
    },
    stopLoading: (state) => {
      state.isLoading = false;
    },
    setReboPipelinePage(state, action: PayloadAction<SetReboPipelinePagePayload>) {
      state.rows = action.payload.rows;
      state.page = action.payload.page;
      state.hasNext = action.payload.hasNext;
      if (action.payload.totalCount !== undefined) {
        state.totalCount = action.payload.totalCount;
      }
      state.isLoading = false;
    },
    setReboPipelineTotalCount(state, action: PayloadAction<number>) {
      state.totalCount = action.payload;
    },
    setReboPipelineRowsPerPage(state, action: PayloadAction<number>) {
      state.rowsPerPage = action.payload;
    },
    clearReboPipeline: (state) => {
      state.rows = [];
      state.page = 0;
      state.hasNext = false;
      state.totalCount = null;
      state.isLoading = false;
      state.hasError = false;
      state.error = '';
    },
    startDetailLoading: (state) => {
      state.detailLoading = true;
      state.detailHasError = false;
      state.detailError = '';
      state.currentDetail = null;
    },
    setReboPipelineDetail(state, action: PayloadAction<SetReboPipelineDetailPayload>) {
      state.currentDetail = action.payload.currentDetail;
      state.detailLoading = false;
    },
    clearReboPipelineDetail: (state) => {
      state.currentDetail = null;
      state.detailLoading = false;
      state.detailHasError = false;
      state.detailError = '';
    },
    setReboPipelineDetailError: (state, action: PayloadAction<string>) => {
      state.detailLoading = false;
      state.detailHasError = true;
      state.detailError = action.payload;
    },
  },
});

export const {
  startLoading,
  stopLoading,
  hasError,
  setReboPipelinePage,
  setReboPipelineTotalCount,
  setReboPipelineRowsPerPage,
  clearReboPipeline,
  startDetailLoading,
  setReboPipelineDetail,
  clearReboPipelineDetail,
  setReboPipelineDetailError,
} = reboPipelineSlice.actions;

export default reboPipelineSlice.reducer;

